// Lyddirektøren oversætter simulationens hændelser og tilstand til lyd, musik og haptik.
// Den er det eneste sted, der ved, hvilken hændelse der lyder hvordan (DESIGN.md: "Lyd"),
// så main og render ikke skal kende til lyd. Ren kosmetik: den læser kun tilstanden.

import type { SimEvent, State } from '../sim';
import { clockFull } from '../sim/cities';
import { isCard } from '../sim/threats';
import { TICK_HZ } from '../sim/types';
import { volumes } from '../storage';
import type { Volumes } from './mixer';
import { Mixer } from './mixer';
import { Music } from './music';
import { Sfx } from './sfx';

/** play: banen kører. calm: menu og forside, rolig musik. off: stille (resultat). */
export type AudioMode = 'play' | 'calm' | 'off';

const COMBO_MS = 2500;

export class Audio {
  readonly mixer: Mixer;
  readonly sfx: Sfx;
  readonly music: Music;
  /** Haptik ved snap og brud (indstillingen Vibration). Kun Android vibrerer; iOS er tavs. */
  haptics = true;
  private combo = 0;
  private comboAt = -1e9;
  private nextTick = 0;
  private mode: AudioMode = 'calm';

  constructor(muted: boolean) {
    this.mixer = new Mixer(volumes(), muted);
    this.sfx = new Sfx(this.mixer);
    this.music = new Music(this.mixer);
  }

  setVolumes(v: Volumes): void {
    this.mixer.setVolumes(v);
  }

  setMuted(m: boolean): void {
    this.mixer.setMuted(m);
  }

  vibrate(pattern: number | number[]): void {
    // Følger kun indstillingen Vibration, ikke lyden: man slår ofte lyden fra og vil stadig mærke den.
    if (!this.haptics) return;
    try {
      navigator.vibrate?.(pattern);
    } catch {
      // Ingen vibration her.
    }
  }

  /** En by tænder: akkorden stiger, når flere tænder kort efter hinanden. */
  cityLit(): void {
    const now = performance.now();
    this.combo = now - this.comboAt < COMBO_MS ? this.combo + 1 : 0;
    this.comboAt = now;
    this.sfx.play('chord', this.combo);
  }

  /** Hændelser fra et tick. `quiet` (attract) spiller kun musik, ingen effekter. */
  events(evs: SimEvent[], brain: boolean, quiet = false): void {
    if (quiet) return;
    const s = this.sfx;
    for (const e of evs) {
      switch (e.t) {
        case 'line_built':
          // Spillerens egne bygninger lyder allerede ved trykket; Volt og autoplay lyder her.
          if (brain) s.play('build');
          break;
        case 'line_upgraded':
          if (brain) s.play('upgrade');
          break;
        case 'line_removed':
          if (brain) s.play('demolish');
          break;
        case 'line_broken':
          s.play('crack');
          this.vibrate([30, 40, 30]);
          break;
        case 'line_saved':
          s.play('ding');
          break;
        case 'line_repaired':
          s.play('crewDone');
          break;
        case 'mast_reinforced':
          s.play('reinforce');
          break;
        case 'crew_dispatched':
        case 'crew_hired':
          s.play('crew');
          break;
        case 'line_overload':
          s.play('hum', Math.min(1, Math.max(0, (e.loadPm - 900) / 400)));
          break;
        case 'blackout':
          s.play('blackout');
          this.vibrate(70);
          break;
        case 'relit':
          s.play('relit');
          break;
        case 'warning':
          // Hver trussel har sin egen lyd, så øret ved, hvad der kommer, før øjet finder den.
          s.play(isCard(e.kind) ? 'flip' : e.kind === 'anchor' ? 'horn' : e.kind === 'digger' ? 'beep' : 'warn');
          break;
        case 'threat_hit':
          if (e.kind === 'storm') s.play('thunder');
          else if (e.kind === 'heatwave') s.play('shimmer');
          else if (e.kind === 'spike') s.play('hum', 0.6);
          else if (e.kind === 'dc_rush') s.play('pop');
          break;
        case 'card_answered':
          // Spillerens svar lander med et pling; et standardsvar (tiden løb ud) kun med et lille vend.
          s.play(e.auto ? 'flip' : 'chime');
          if (!e.auto && e.cost > 0) s.play('coin');
          break;
        case 'power_used':
          s.play(e.power === 'fcr' ? 'zap' : e.power === 'shed' ? 'powerDown' : 'norway');
          this.vibrate(e.power === 'shed' ? 40 : [20, 30, 20]);
          break;
        case 'power_ended':
          s.play('whoosh');
          break;
        case 'phantom_vanished':
          s.play('pop');
          break;
        case 'threat_shooed':
          s.play('pop');
          if (e.kind === 'smaa') s.play('coin');
          break;
        case 'smaa':
          s.play('whoosh');
          break;
        case 'income_tick':
          s.play('clink');
          break;
        case 'rescue':
          s.play('phew');
          break;
        case 'quick_connect':
          s.play('lyn');
          break;
        case 'producer_connected':
        case 'connection_fee':
        case 'advance':
          s.play('coin');
          break;
        case 'year_end':
          s.play('fanfare');
          break;
        case 'finale':
          this.music.finale = true;
          s.play('warn');
          break;
        case 'year_start':
          this.music.finale = e.finale;
          break;
        case 'level_won':
          s.play('win');
          this.music.resolve();
          this.mode = 'off';
          break;
        case 'level_lost':
          s.play('lose');
          this.music.stop();
          this.mode = 'off';
          break;
      }
    }
  }

  setMode(mode: AudioMode): void {
    if (mode === this.mode) return;
    this.mode = mode;
    if (mode === 'off') this.music.stop();
    if (mode !== 'play') {
      this.music.finale = false;
      this.music.tension = 0;
    }
  }

  /** Hver frame: musikkens lag og mørkeurets tik. `running` er falsk under pause og skærme. */
  frame(st: State | null, dt: number, running: boolean): void {
    const mu = this.music;
    if (this.mode !== 'off') mu.start();
    if (st && this.mode === 'play') {
      let lit = 0;
      let worst = 0;
      const full = clockFull(st);
      for (const n of st.nodes) {
        const c = n.city;
        if (!n.active || !c) continue;
        if (!c.out && c.demandMw > 0 && c.servedMw >= c.demandMw * 0.9) lit++;
        if (!c.out && full > 0) worst = Math.max(worst, c.clock / full);
      }
      let storm = 0;
      for (const t of st.threats) {
        // Kortene er beslutninger, ikke uvejr: de løfter musikken lidt, ikke helt.
        if (isCard(t.kind)) storm = Math.max(storm, t.phase === 'done' ? 0 : 0.35);
        else if (t.phase === 'active') storm = Math.max(storm, 0.8);
        else if (t.phase === 'warning') {
          const left = (t.hitAt - st.tick) / TICK_HZ;
          storm = Math.max(storm, left < 6 ? 0.7 : 0.45);
        }
      }
      const broken = st.lines.filter((l) => l.status === 'broken').length;
      mu.cities = lit;
      mu.tension = Math.min(1, Math.max(storm, worst * 0.95, broken * 0.2, st.finale ? 0.8 : 0));
      // Mørkeur: tik, der bliver hurtigere fra 75 %.
      const now = performance.now();
      if (running && worst >= 0.75 && now >= this.nextTick) {
        const f = Math.min(1, (worst - 0.75) / 0.25);
        this.sfx.play('tick', f);
        this.nextTick = now + 600 - 420 * f;
      }
    } else if (st) {
      // Forside og menuer: rolig loop med de byer, der lyser bagved.
      mu.cities = st.nodes.filter((n) => n.city && !n.city.out && n.city.servedMw >= n.city.demandMw * 0.9 && n.city.demandMw > 0).length;
      mu.tension = 0;
    }
    mu.update(dt / 1000);
  }
}
